import { useState, useEffect } from 'react';
import {
  useAdminAgencies,
  useDeleteAgency,
  useCreateAgency,
  useAdminPackages,
} from '@/hooks/useAdmin';
import { AdminAgencyListFilters, AdminAgencyListItem, CreateAgencyRequest } from '@/types/admin';
import { CreateAgencyResponse } from '@/services/admin.service';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ActionButton } from '@/components/admin/ActionButton';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { useTimezone } from '@/contexts/TimezoneContext';
import { toast } from '@/utils/toast';
import { ICONS } from '@/constants/icons';
import { DatePickerSingle } from '@/components/ui/DatePickerSingle';
import { Modal } from '@/components/ui/Modal';
import { ModalFooter } from '@/components/ui/ModalFooter';
import { ConfirmDialog, Card, EmptyState, Pagination } from '@/components/ui';
import { Select } from '@/components/ui/Select';
import { formatApiError } from '@/services/api';
import { useDebounce } from '@/hooks/useDebounce';

/**
 * Admin Agencies Page
 * List and manage all agencies
 */

export const AdminAgencies = () => {
  const { formatDateOnly } = useTimezone();
  const [filters, setFilters] = useState<AdminAgencyListFilters>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  useEffect(() => {
    setFilters((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [debouncedSearch]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; agency: AdminAgencyListItem | null }>({
    open: false,
    agency: null,
  });

  const { data, isLoading, error } = useAdminAgencies({
    ...filters,
    search: debouncedSearch || undefined,
  });
  const deleteAgency = useDeleteAgency();
  const createAgency = useCreateAgency();

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.agency) return;

    try {
      await deleteAgency.mutateAsync(deleteDialog.agency.id);
      toast.success(`Brand Portfolio ${deleteDialog.agency.name} deleted`);
      setDeleteDialog({ open: false, agency: null });
    } catch (err) {
      toast.error('Failed to delete brand portfolio');
    }
  };

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
  };

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        Error loading agencies: {formatApiError(error).message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Brand Portfolios"
        subtitle="Manage Atelier Mode brand portfolios"
        action={<Button onClick={() => setShowCreateModal(true)}>Create Brand Portfolio</Button>}
      />

      {/* Search & Filters */}
      <Card padding="sm">
        <form onSubmit={(e) => e.preventDefault()} className="flex gap-4 flex-wrap">
          <Input
            type="text"
            placeholder="Search by name or slug..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[200px]"
          />
        </form>
      </Card>

      {/* Agencies Table */}
      <Card padding="none" className="overflow-hidden">
        {isLoading ? (
          <div className="p-8 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                      Brand Portfolio
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                      Slug
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                      Clients
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                      Staff
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                      Owner
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">
                      Created
                    </th>
                    <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(data?.data ?? []).map((agency) => (
                    <tr key={agency.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {agency.name}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {agency.slug}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {agency.clientCount}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {agency.staffCount}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {agency.ownerEmail || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDateOnly(agency.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <ActionButton
                            icon={ICONS.EYE}
                            tooltip="View Details"
                            href={`/admin/agencies/${agency.id}`}
                          />
                          <ActionButton
                            icon={ICONS.DELETE}
                            tooltip="Delete Brand Portfolio"
                            onClick={() => setDeleteDialog({ open: true, agency })}
                            variant="danger"
                            disabled={deleteAgency.isPending}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(data?.data?.length ?? 0) === 0 && (
                    <tr>
                      <td colSpan={7}>
                        <EmptyState icon={ICONS.TEAM} title="No brand portfolios found" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data?.meta && data.meta.totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing {(data.meta.page - 1) * data.meta.limit + 1} to{' '}
                  {Math.min(data.meta.page * data.meta.limit, data.meta.total)}{' '}
                  of {data.meta.total}
                </div>
                <Pagination
                  page={data.meta.page}
                  totalPages={data.meta.totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Create Agency Modal */}
      {showCreateModal && (
        <CreateAgencyModal
          onClose={() => setShowCreateModal(false)}
          createAgency={createAgency}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => !open && setDeleteDialog({ open: false, agency: null })}
        title="Delete Brand Portfolio"
        description={`Are you sure you want to delete "${deleteDialog.agency?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        loading={deleteAgency.isPending}
      />
    </div>
  );
};

// ============================================================================
// CREATE AGENCY MODAL
// ============================================================================

interface CreateAgencyModalProps {
  onClose: () => void;
  createAgency: ReturnType<typeof useCreateAgency>;
}

const CreateAgencyModal = ({
  onClose,
  createAgency,
}: CreateAgencyModalProps) => {
  const [formData, setFormData] = useState<CreateAgencyRequest>({
    name: '',
    email: '',
    packageId: 0,
    ownerName: '',
  });

  const { data: packagesData } = useAdminPackages({ isActive: true });
  const allPackages = (packagesData as { data?: { id: number; name: string; isAgency?: boolean }[] })?.data || [];
  // Only show agency packages for agencies
  const packages = allPackages.filter(pkg => pkg.isAgency);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Brand Portfolio name is required');
      return;
    }

    if (!formData.email.trim()) {
      toast.error('Email is required');
      return;
    }

    if (!formData.packageId) {
      toast.error('Please select a package');
      return;
    }

    try {
      const result = await createAgency.mutateAsync(formData) as CreateAgencyResponse;
      toast.success(`Brand Portfolio "${result.agency.name}" created! Password setup email sent to ${result.user.email}`);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err).message);
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Create New Brand Portfolio"
      size="sm"
      footer={
        <ModalFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={createAgency.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" form="create-agency-form" loading={createAgency.isPending}>
            Create Brand Portfolio
          </Button>
        </ModalFooter>
      }
    >
      <form id="create-agency-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Brand Portfolio Name *
          </label>
          <Input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Acme Brand Studio"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email *
          </label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="owner@agency.com"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Password setup email will be sent to this address
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Owner Name (optional)
          </label>
          <Input
            type="text"
            value={formData.ownerName || ''}
            onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
            placeholder="John Doe"
          />
          <p className="text-xs text-gray-500 mt-1">
            Defaults to brand portfolio name if not provided
          </p>
        </div>

        <div>
          <Select
            label="Package *"
            placeholder="Select a package..."
            value={formData.packageId === 0 ? '' : String(formData.packageId)}
            onValueChange={(value) => setFormData({ ...formData, packageId: parseInt(value, 10) })}
            options={packages.map((pkg) => ({
              value: String(pkg.id),
              label: pkg.name,
            }))}
          />
        </div>

        <div>
          <DatePickerSingle
            label="Expiry Date (optional)"
            value={formData.expiresAt || ''}
            onChange={(iso) => setFormData({ ...formData, expiresAt: iso || null })}
            placeholder="dd.mm.yyyy"
            minDate={new Date()}
          />
          <p className="text-xs text-gray-500 mt-1">
            Leave empty for no expiry
          </p>
        </div>
      </form>
    </Modal>
  );
};

export default AdminAgencies;
